/**
 * Two-step PIN creation component (enter + confirm).
 *
 * State machine: enter -> confirm -> calls onComplete(pin).
 * If confirm doesn't match: shows error, resets to enter step.
 *
 * Tier: All tiers (core infrastructure, used during wallet creation).
 *
 * @module PinSetup
 */

import { useCallback, useState } from 'react';
import { PinEntry } from './pin-entry';

/** Props for PinSetup component. */
interface PinSetupProps {
  /** Called when the PIN has been successfully confirmed. */
  readonly onComplete: (pin: string) => void;
  /** Whether the component is in a loading state. */
  readonly loading?: boolean;
}

/** PIN setup step state. */
type PinSetupStep = 'enter' | 'confirm';

/**
 * Two-step PIN creation: enter then confirm.
 *
 * Renders PinEntry with appropriate title for each step.
 * Resets to enter step if confirmation doesn't match.
 *
 * Tier: All tiers (core infrastructure).
 */
export function PinSetup({ onComplete, loading = false }: PinSetupProps): React.ReactElement {
  const [step, setStep] = useState<PinSetupStep>('enter');
  const [firstPin, setFirstPin] = useState<string>('');
  const [error, setError] = useState<string | undefined>(undefined);

  const handleEnterComplete = useCallback((pin: string): void => {
    setFirstPin(pin);
    setError(undefined);
    setStep('confirm');
  }, []);

  const handleConfirmComplete = useCallback(
    (pin: string): void => {
      if (pin === firstPin) {
        onComplete(pin);
      } else {
        setError("PINs don't match");
        setFirstPin('');
        setStep('enter');
      }
    },
    [firstPin, onComplete],
  );

  if (step === 'enter') {
    return (
      <PinEntry
        key="enter"
        title="Set your PIN"
        onComplete={handleEnterComplete}
        error={error}
        loading={loading}
      />
    );
  }

  return (
    <PinEntry
      key="confirm"
      title="Confirm your PIN"
      onComplete={handleConfirmComplete}
      loading={loading}
    />
  );
}

export type { PinSetupProps };
